import React, { useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'

import useAutoCoder, {
  SendToPipelineTarget,
  sendTargetIsEmpty,
  useSendToPipeline,
} from '../../../hooks/useAutoCoder'
import { SubmitBatchesResult, AutoCoderPipeline } from '../../../types/gotypes'

/**
 * THE SEND, as a dialog.
 *
 * A fetch produces records that belong to the job, not to any pipeline; this is
 * the deliberate act that puts a chosen slice of them into one — the only way
 * records enter a pipeline, and the moment somebody starts paying for model
 * calls. It is therefore explicit, single-target, and it reports exactly what
 * the server did rather than saying "done".
 *
 * The funnel is rendered verbatim because its parts mean different things: a
 * send that matched 500 records and opened 3 codings is not a failure — 497
 * were already in flight and the in-flight unique index refused to double-bill
 * them. A send that matched NOTHING is not a success, and says so.
 */

interface SendToPipelineDialogProps {
  customer: string
  job: string
  /** What to send; null closes the dialog (and unmounts the pipelines query). */
  target: SendToPipelineTarget | null
  /** One line of human context for the header, e.g. the receipt's timestamp. */
  targetDescription?: string
  onClose: () => void
}

const pipelineLabel = (pipeline: AutoCoderPipeline): string =>
  pipeline.config?.name || pipeline.system_name || pipeline.id

/** The counts, in the order they happen, each with the reading it actually has. */
const funnelRows = (result: SubmitBatchesResult): Array<[string, number, string]> => [
  ['Batches created', result.batchesCreated, 'Coding batches this send created.'],
  ['Codings opened', result.codingsOpened, 'New pending codings this send created.'],
  [
    'Already in flight',
    result.alreadyInFlight,
    'Matched records this pipeline was already coding — not sent again.',
  ],
  [
    'Batches dropped',
    result.batchesDropped,
    'Batches that ended up owning no codings (every record they named was already in flight) and were removed.',
  ],
]

const SendToPipelineDialog: React.FC<SendToPipelineDialogProps> = ({
  customer,
  job,
  target,
  targetDescription,
  onClose,
}) => {
  const [pipelineId, setPipelineId] = useState<string | null>(null)

  // Mounted only while the dialog is open (the parent renders it on a target),
  // so the pipelines list is fetched when somebody is choosing, not on the tab.
  const { pipelines, pipelinesLoading, pipelinesError } = useAutoCoder({
    customer,
    job,
    lifecycle: 'any',
    rowsPerPage: 500,
  })
  const { send, result, progress, sending, error, reset } = useSendToPipeline(customer, job)

  const close = () => {
    setPipelineId(null)
    reset()
    onClose()
  }

  const selected = pipelines.find((p: AutoCoderPipeline) => p.id === pipelineId)
  const nothingToSend = sendTargetIsEmpty(target)

  return (
    <Dialog
      open={!!target}
      onClose={close}
      maxWidth="sm"
      fullWidth
      data-testid="autocoder-send-dialog"
    >
      <DialogTitle>
        Send to pipeline
        {targetDescription && (
          <Typography variant="body2" color="text.secondary">
            {targetDescription}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {nothingToSend && (
          <Alert severity="warning">
            There is nothing selected to send. Pick a receipt or some entities first.
          </Alert>
        )}

        {pipelinesError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String(pipelinesError)}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Before the send: choose where the records are going. */}
        {!result && !nothingToSend && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              These records will be coded by the pipeline you choose, using its frameworks. Coding
              costs model calls.
            </Typography>

            {pipelinesLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress color="info" />
              </Box>
            )}

            {!pipelinesLoading && pipelines.length === 0 && (
              <Alert severity="info">
                This job has no pipelines yet. Create one first, then send this data to it.
              </Alert>
            )}

            {pipelines.length > 0 && (
              <List dense sx={{ maxHeight: '45vh', overflow: 'auto' }} data-testid="autocoder-send-pipeline-list">
                {pipelines.map((pipeline: AutoCoderPipeline) => (
                  <ListItemButton
                    key={pipeline.id}
                    selected={pipeline.id === pipelineId}
                    onClick={() => setPipelineId(pipeline.id)}
                    // The pipeline id, not the label: `pipelineLabel` already
                    // falls back through three fields, so the visible name is
                    // the least stable thing about a row here.
                    data-testid={`autocoder-send-pipeline-${pipeline.id}`}
                  >
                    <ListItemText
                      primary={pipelineLabel(pipeline)}
                      secondary={`${pipeline.config?.frameworks?.length ?? 0} framework(s)`}
                    />
                    {pipeline.lifecycle && (
                      <Chip size="small" variant="outlined" label={pipeline.lifecycle} />
                    )}
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        )}

        {/* While a multi-pass send is looping: live totals so far. */}
        {sending && progress && (
          <Alert severity="info" icon={<CircularProgress size={16} color="inherit" />} sx={{ mb: 2 }}>
            Sending… {progress.codingsOpened.toLocaleString()} coding(s) opened so far
            {progress.remaining > 0 ? ', more to go' : ''}.
          </Alert>
        )}

        {/* After the send: what the server actually did. */}
        {result && (
          <Stack spacing={2} data-testid="autocoder-send-result">
            {result.codingsOpened === 0 && result.alreadyInFlight === 0 ? (
              <Alert severity="warning">
                <AlertTitle>Nothing matched</AlertTitle>
                Nothing was sent{selected ? ` to ${pipelineLabel(selected)}` : ''}. The selection
                resolved to no records in this job — nothing is being coded and nothing was billed.
              </Alert>
            ) : (
              <Alert severity={result.codingsOpened > 0 ? 'success' : 'info'}>
                <AlertTitle>
                  {result.codingsOpened > 0
                    ? `${result.codingsOpened.toLocaleString()} coding(s) opened`
                    : 'No new codings opened'}
                </AlertTitle>
                {result.codingsOpened > 0
                  ? `Queued for ${selected ? pipelineLabel(selected) : 'the pipeline'}. Coding starts under the pipeline's own gates.`
                  : 'Every matched record was already in flight in this pipeline, so nothing was sent again.'}
              </Alert>
            )}

            <Box>
              {funnelRows(result).map(([label, value, help]) => (
                <Stack
                  key={label}
                  direction="row"
                  alignItems="baseline"
                  spacing={2}
                  sx={{ py: 0.5 }}
                >
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2">{label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {help}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: value > 0 ? 600 : 400 }}>
                    {value.toLocaleString()}
                  </Typography>
                </Stack>
              ))}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="info" onClick={close} data-testid="autocoder-send-close">
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            color="info"
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={!pipelineId || sending || nothingToSend}
            onClick={() => {
              if (!pipelineId || !target) return
              void send(pipelineId, target).catch(() => {
                // Surfaced by the mutation's error alert.
              })
            }}
            data-testid="autocoder-send-confirm"
          >
            Send
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default SendToPipelineDialog
